import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';

/**
 * Protected Route component that checks if user is authenticated
 * and has the required role to access the route
 */
const ProtectedRoute = ({ 
  allowedRoles = [], 
  redirectPath = '/login',
  children 
}) => {
  const { user, loading, hasRole } = useAuth();
  const location = useLocation();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center', 
          height: '100vh' 
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary" mt={2}>
          Loading...
        </Typography>
      </Box>
    );
  }
  
  // Check if user is authenticated
  if (!user) {
    return <Navigate to={redirectPath} replace />;
  }

  if (user.mustChangePassword && location.pathname !== '/profile') {
    return <Navigate to="/profile" replace />;
  }
  
  // Check if user has required role (if specified)
  if (allowedRoles.length > 0 && !hasRole(allowedRoles)) {
    // Redirect based on user role
    if (user.role === 'admin' || user.role === 'operator') {
      return <Navigate to="/sites" replace />;
    } else if (user.role === 'super_user') {
      return <Navigate to="/sites" replace />;
    } else {
      return <Navigate to="/devices" replace />;
    }
  }
  
  // Render children or outlet
  return children ? children : <Outlet />;
};

export default ProtectedRoute;
